import { FC, useState } from 'react'
import clsx from 'clsx'
import styles from './ShareBar.module.css'
import { BasicStyledComponent } from 'shared-definitions/types'
import ThreeDotsShare from 'shared-svg/ThreeDotsShare.svg'
import { useOutsideClick } from '../hooks/use-outside-click'
import { useCopy } from '../hooks/use-copy'
import ShareMenu from './ShareMenu'

// Article-level share: the three-dots trigger in the post short line.
// Menu width plus its left offset — see ShareMenu.module.css `.menu` / `.alignLeft`.
const menuFootprint = 220

/** Native share menu: Facebook / X / Email / Copy URL. */
const ShareBar: FC<BasicStyledComponent> = ({ className }) => {
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [align, setAlign] = useState<'left' | 'right'>('left')
  const { copy } = useCopy()
  const ref = useOutsideClick<HTMLDivElement>(() => {
    setShareUrl(null)
  }, [])

  const toggle = (): void => {
    // The trigger can sit anywhere on the byline — mid-line on desktop, hard
    // against the right edge on a phone. A left-hung menu there overflows the
    // viewport, the page gets wider and the browser scales the layout down, so
    // hang it from whichever side has the room, measured at open time.
    const rect = ref.current?.getBoundingClientRect()
    setAlign(rect && rect.left + menuFootprint > window.innerWidth ? 'right' : 'left')
    // Canonical page URL — no hash, no tracking query — captured at open time
    // so a client-side navigation never shares the previous article.
    setShareUrl(url => (url ? null : window.location.origin + window.location.pathname))
  }

  return (
    <div
      className={clsx(styles.container, className)}
      ref={ref}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setShareUrl(null)
        }
      }}
    >
      <button
        type="button"
        className={styles.showButton}
        aria-label="Share"
        aria-haspopup="menu"
        aria-expanded={shareUrl !== null}
        onClick={toggle}
      >
        <ThreeDotsShare className={styles.showButtonIcon} />
      </button>
      {shareUrl !== null ? (
        <ShareMenu
          align={align}
          ariaLabel="Share article"
          url={shareUrl}
          title={document.title}
          onSelect={() => setShareUrl(null)}
          onCopy={() => void copy(shareUrl)}
        />
      ) : null}
    </div>
  )
}

export default ShareBar
